/**
 * 数据维护首页组件
 */

import React, { useState, useEffect } from 'react';
import { Card, Typography, Row, Col, Space, Statistic, Button } from 'antd';
import { 
  UserOutlined, 
  TeamOutlined, 
  MenuOutlined, 
  FileTextOutlined,
  BarChartOutlined,
  SettingOutlined,
  ClockCircleOutlined,
  ApiOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { logService } from '../../../../../services/logService';
import { LogStatistics } from '../../../../../types/log';

const { Title, Paragraph } = Typography;

const DataHome: React.FC = () => {
  const navigate = useNavigate();
  const [logStatistics, setLogStatistics] = useState<LogStatistics | null>(null);
  const [loading, setLoading] = useState(false);

  // 加载日志统计数据
  const loadLogStatistics = async () => {
    setLoading(true);
    try {
      const response = await logService.getStatistics();
      if (response.code === 200) {
        setLogStatistics(response.data);
      }
    } catch (error) {
      console.error('加载日志统计失败:', error);
    } finally {
      setLoading(false);
    }
  };

  // 组件挂载时加载统计数据
  useEffect(() => {
    loadLogStatistics();
  }, []);

  // 快速导航功能
  const quickActions = [
    {
      title: '平台简介管理',
      description: '管理平台简介内容和统计数据',
      icon: <FileTextOutlined style={{ fontSize: '24px', color: '#13c2c2' }} />,
      path: '/data/platform',
      color: '#13c2c2'
    },
    {
      title: '用户管理',
      description: '管理系统用户账号和权限',
      icon: <UserOutlined style={{ fontSize: '24px', color: '#1890ff' }} />,
      path: '/data/user',
      color: '#1890ff'
    },
    {
      title: '角色管理',
      description: '配置用户角色和权限',
      icon: <TeamOutlined style={{ fontSize: '24px', color: '#52c41a' }} />,
      path: '/data/role',
      color: '#52c41a'
    },
    {
      title: '菜单管理',
      description: '管理系统菜单和导航',
      icon: <MenuOutlined style={{ fontSize: '24px', color: '#faad14' }} />,
      path: '/data/menu',
      color: '#faad14'
    },
    {
      title: '字典管理',
      description: '维护系统字典数据',
      icon: <FileTextOutlined style={{ fontSize: '24px', color: '#722ed1' }} />,
      path: '/data/dict',
      color: '#722ed1'
    },
    {
      title: '日志管理',
      description: '查看系统操作日志',
      icon: <BarChartOutlined style={{ fontSize: '24px', color: '#f5222d' }} />,
      path: '/data/log',
      color: '#f5222d'
    },
    {
      title: '权限管理',
      description: '配置系统权限设置',
      icon: <SettingOutlined style={{ fontSize: '24px', color: '#13c2c2' }} />,
      path: '/data/permissions',
      color: '#13c2c2'
    }
  ];

  const handleQuickAction = (path: string) => {
    navigate(path);
  };


  return (
    <div style={{ padding: '24px', background: '#f0f2f5', minHeight: 'calc(100vh - 64px)' }}>
      {/* 欢迎区域 */}
      <Card style={{ marginBottom: '24px', textAlign: 'center' }}>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <Title level={1} style={{ margin: 0, color: '#1890ff' }}>
            欢迎使用数据维护平台
          </Title>
          <Paragraph style={{ fontSize: '16px', color: '#666', margin: 0 }}>
            这里是数据维护平台的管理中心，您可以在这里管理系统的各种数据和配置
          </Paragraph>
        </Space>
      </Card>

      {/* 日志统计 */}
      {logStatistics && (
        <Card title="日志统计" style={{ marginBottom: '24px' }}>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={6}>
              <Card>
                <Statistic
                  title="总日志数"
                  value={logStatistics.totalCount}
                  prefix={<BarChartOutlined />}
                  loading={loading}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card>
                <Statistic
                  title="今日日志"
                  value={logStatistics.todayCount}
                  prefix={<ClockCircleOutlined />}
                  loading={loading}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card>
                <Statistic
                  title="成功操作"
                  value={logStatistics.successCount}
                  valueStyle={{ color: '#3f8600' }}
                  prefix={<UserOutlined />}
                  loading={loading}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card>
                <Statistic
                  title="失败操作"
                  value={logStatistics.failedCount}
                  valueStyle={{ color: '#cf1322' }}
                  prefix={<ApiOutlined />}
                  loading={loading}
                />
              </Card>
            </Col>
          </Row>
        </Card>
      )}

      {/* 系统概览 */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="系统状态"
              value="正常"
              valueStyle={{ color: '#3f8600' }}
              prefix={<div style={{ 
                width: '8px', 
                height: '8px', 
                backgroundColor: '#52c41a', 
                borderRadius: '50%',
                display: 'inline-block',
                marginRight: '8px'
              }} />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="在线用户"
              value={1}
              suffix="人"
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="今日操作"
              value={0}
              suffix="次"
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="系统版本"
              value="v1.0.0"
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
      </Row>

      {/* 快速操作
      <Card title="快速操作" style={{ marginBottom: '24px' }}>
        <Row gutter={[16, 16]}>
          {quickActions.map((action, index) => (
            <Col xs={24} sm={12} md={8} lg={6} key={index}>
              <Card
                hoverable
                style={{ 
                  textAlign: 'center',
                  border: `1px solid ${action.color}20`,
                  backgroundColor: `${action.color}05`
                }}
                styles={{ body: { padding: '20px' } }}
                onClick={() => handleQuickAction(action.path)}
              >
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                  {action.icon}
                  <div>
                    <Title level={4} style={{ margin: 0, color: action.color }}>
                      {action.title}
                    </Title>
                    <Paragraph 
                      style={{ 
                        margin: '8px 0 0 0', 
                        fontSize: '12px', 
                        color: '#666',
                        minHeight: '32px'
                      }}
                    >
                      {action.description}
                    </Paragraph>
                  </div>
                  <Button 
                    type="primary" 
                    size="small"
                    style={{ backgroundColor: action.color, borderColor: action.color }}
                  >
                    进入管理
                  </Button>
                </Space>
              </Card>
            </Col>
          ))}
        </Row>
      </Card> */}
    </div>
  );
};

export default DataHome;